import React, { useMemo } from 'react';
import {
  Target,
  LayoutGrid,
  Image as ImageIcon,
  Search,
  SearchX,
  Quote,
  MapPin,
  Tag,
  Package,
  Layers,
  Building2,
  Users,
  Dot,
  ChevronRight,
} from 'lucide-react';

const ICON_BY_TYPE = {
  CAMPAIGN: Target,
  AD_GROUP: LayoutGrid,
  AD: ImageIcon,
  KEYWORD: Search,
  NEGATIVE_KEYWORD: SearchX,
  SEARCH_TERM: Quote,
  PLACEMENT: MapPin,
  OFFER: Tag,
  PRODUCT: Package,
  ASSET: Package,
  CREATIVE: ImageIcon,
  ASSET_GROUP: Layers,
  ACCOUNT: Building2,
  AUDIENCE: Users,
};

const LABEL_BY_TYPE = {
  CAMPAIGN: 'Campaña',
  AD_GROUP: 'AdSet',
  AD: 'Anuncio',
  KEYWORD: 'Keyword',
  NEGATIVE_KEYWORD: 'Keyword negativa',
  SEARCH_TERM: 'Search term',
  PLACEMENT: 'Placement',
  OFFER: 'Producto',
  PRODUCT: 'Producto',
  ASSET: 'Asset',
  CREATIVE: 'Creatividad',
  ASSET_GROUP: 'Grupo de assets',
  ACCOUNT: 'Cuenta',
  AUDIENCE: 'Audiencia',
};

const compactBreadcrumb = (crumbs) => {
  if (!Array.isArray(crumbs) || crumbs.length === 0) return null;
  if (crumbs.length <= 3) return crumbs;
  return [crumbs[0], '…', crumbs[crumbs.length - 1]];
};

export default function CreativePreview({
  entity,
  subtitle,
  size = 'lg',
  entityTypeLabel,
}) {
  const typeRaw = entity?.type ? String(entity.type).toUpperCase() : null;
  const label = entityTypeLabel
    || (typeRaw && LABEL_BY_TYPE[typeRaw])
    || typeRaw
    || 'Entidad';

  const Icon = (typeRaw && ICON_BY_TYPE[typeRaw]) || Dot;
  const thumbnailUrl = entity?.thumbnail_url || null;
  const entityUrl = entity?.permalink_url || null;

  const dims = size === 'md'
    ? { box: 'w-9 h-9', icon: 'w-[18px] h-[18px]', t1: 'text-[9px]', t2: 'text-[13px]', t3: 'text-[11px]' }
    : { box: 'w-11 h-11', icon: 'w-[22px] h-[22px]', t1: 'text-[10px]', t2: 'text-[15px]', t3: 'text-[12px]' };

  const segments = useMemo(() => {
    if (Array.isArray(subtitle)) return compactBreadcrumb(subtitle);
    if (typeof subtitle === 'string' && subtitle.trim()) return [subtitle];
    return null;
  }, [subtitle]);

  const nameClass = `${dims.t2} font-medium text-white mt-[2px] truncate`;
  const nameNode = entity?.name ? (
    entityUrl ? (
      <a
        href={entityUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`${nameClass} block hover:underline`}
        title={entity.name}
      >
        {entity.name}
      </a>
    ) : (
      <div className={nameClass} title={entity.name}>
        {entity.name}
      </div>
    )
  ) : (
    <div className={`${dims.t2} font-medium text-white/70 mt-[2px] truncate`}>
      Entidad sin identificar
    </div>
  );

  return (
    <div className="flex items-start gap-3 min-w-0">
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={entity?.name || label}
          className={`${dims.box} shrink-0 rounded-lg object-cover border border-white/10 bg-white/5`}
        />
      ) : (
        <div
          className={`${dims.box} shrink-0 rounded-lg flex items-center justify-center border border-white/10 bg-white/[0.06]`}
        >
          <Icon className={dims.icon} style={{ color: 'rgba(255,255,255,0.85)' }} aria-hidden />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className={`${dims.t1} font-medium tracking-[0.6px] uppercase text-white/50 leading-none`}>
          {label}
        </div>
        {nameNode}
        {segments && segments.length > 0 ? (
          <div className={`${dims.t3} text-white/70 mt-[2px] flex items-center min-w-0`}>
            {segments.map((c, idx) => (
              <React.Fragment key={`${idx}-${c}`}>
                <span className="truncate">{c}</span>
                {idx < segments.length - 1 ? (
                  <ChevronRight className="w-[10px] h-[10px] mx-1 shrink-0 text-white/40" aria-hidden />
                ) : null}
              </React.Fragment>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
